import Route from '@ember/routing/route';
import { service } from '@ember/service';
import { findRecord } from '@warp-drive/utilities/json-api';
import { isLrapEnrolled } from 'tio-employee/utils/lrap-enrollment';
import type { Store } from '@warp-drive/core';
import type RouterService from '@ember/routing/router-service';
import type SessionContextService from 'tio-employee/services/session-context';
import type FeaturesService from 'tio-common/services/features';
import type LrapEnrollmentService from 'tio-employee/services/lrap-enrollment';
import type { PersonExtension as PersonModel } from 'tio-data/data/resources/person.ext';

export default class AuthenticatedSlrLrapEnrollmentRoute extends Route {
  @service declare store: Store;
  @service declare router: RouterService;
  @service declare sessionContext: SessionContextService;
  @service declare features: FeaturesService;
  @service declare lrapEnrollment: LrapEnrollmentService;

  async beforeModel() {
    if (this.features.isDisabled('lrapEnrollment')) {
      this.router.transitionTo('authenticated.slr.dashboard');
      return;
    }

    // Already-enrolled users go back to the dashboard (which shows their
    // enrolled state) instead of re-entering the wizard. fetchStatus fails
    // open (null), so an outage never locks eligible users out of enrolling.
    const status = await this.lrapEnrollment.fetchStatus();
    if (isLrapEnrolled(status)) {
      this.router.transitionTo('authenticated.slr.dashboard');
      return;
    }
  }

  async model() {
    const personId = this.sessionContext.person.id;
    if (!personId) {
      throw new Error('Cannot load LRAP enrollment without a person id.');
    }
    const { content } = (await this.store.request(
      findRecord<PersonModel>('person', personId, {
        include: 'email-addresses,postal-addresses',
        reload: true,
      })
    )) as { content: { data: PersonModel } };
    const person = content.data;
    return { person };
  }
}

export interface AuthenticatedSlrLrapEnrollmentRouteModel {
  person: PersonModel;
}
